import { hydrateGridCandidate } from '~/services/catalogue'
import {
  deriveMainProductCandidates,
  type HeroDisplay,
  type MainProductSource,
} from '~/services/main-products'
import {
  createHydratedSlots,
  type CatalogueGridSlot,
  type HydrationGate,
} from './catalogue-grid'

// One main-product hero: the shared settle-tracking slot (title and product
// art fill in place, exactly like a Classic slot) plus the hero-only facts
// that never change after derivation.
export interface HeroSlot {
  display: HeroDisplay
  // The Store product id, known from bootstrap: banner diagnostics name it
  // before (and whether or not) the slot's product hydrates.
  productId: number
  purchaseEndAtMs?: number
  slot: CatalogueGridSlot
}

// Named apart from the HeroRow component (components/hero-row.tsx): this is
// the row's slot state, that is its rendering.
export interface HeroRowState {
  // The reveal gate's view of the row: every slot joins the Featured
  // landing's reveal surface (GLOSSARY.md "Reveal surface").
  gridSlots: CatalogueGridSlot[]
  heroSlots: HeroSlot[]
}

// The hero row reuses the Classic slot machinery (GLOSSARY.md "Main product
// collection"): fixed slots in display order, each filling in place as its
// own native lookups settle. Tree and land-skin heroes hydrate through the
// one shared path — the candidate's content decides its title key and art
// source. nowMs is the Shop server clock at creation; the gate is the Shop
// hydration order's (GLOSSARY.md).
export function createHeroRow(
  source: MainProductSource,
  nowMs: number,
  options?: { gate?: HydrationGate },
): HeroRowState {
  const candidates = deriveMainProductCandidates(source, nowMs)
  const { slots: gridSlots } = createHydratedSlots(
    candidates,
    (candidate) => hydrateGridCandidate(candidate, 'shop_main_product_omitted'),
    options,
  )
  return {
    gridSlots,
    heroSlots: candidates.map((candidate, index) => ({
      display: candidate.display,
      productId: candidate.product.id,
      purchaseEndAtMs: candidate.purchaseEndAtMs,
      slot: gridSlots[index]!,
    })),
  }
}
